import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { faCaretDown, faLock } from '@fortawesome/free-solid-svg-icons';
import { Box, ColorNames, ColorShades, Utils } from 'marketpro-design-lib';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { constants } from '../../../../constants';
import { AuthService } from '../../../auth/services/auth.service';
import { GroupedResponse } from '../../models/grouped-statistics-response.model';
import { SimpleStatisticsResponse } from '../../models/simple-statistics-response.model';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { DataService } from '../../services/data.service';
import { DataUtils } from '../../services/DataUtils';
import { CitiesService } from '../../services/cities.service';
import { StatisticsService } from '../../services/statistics.service';
import { SpendingGroup } from '../../models/spending-group.model';
import { debounceTimer } from 'projects/marketpro-design-lib/src';
import { ConsumerDataDetails } from '../../models/statistics.model';
import { AuthorizedResourcesService } from '../../../auth/services/authorized-resources.service';

@Component({
  selector: 'mpro-consumer-spending',
  templateUrl: './consumer-spending.component.html',
  styleUrls: ['./consumer-spending.component.scss'],
})
export class ConsumerSpendingComponent implements OnInit, OnDestroy {
  faCaretDown = faCaretDown;

  startYear: number = constants.initialYear;
  endYear: number = constants.initialYear + 1;
  currentYear: number = constants.initialYear;

  peopleGrowth: number = 0;
  spendingGrowth: number = 0;
  spendingPerCapitaGrowth: number = 0;

  startYearOpen: boolean = false;
  endYearOpen: boolean = false;

  years: number[] = [];
  startYears: number[] = [];
  endYears: number[] = [];

  public isCategoriesEnabled: boolean;

  public totalDataCacheByYear: GroupedResponse<SimpleStatisticsResponse>;
  public dataCacheByYear: GroupedResponse<SimpleStatisticsResponse>;
  public headCount: number;
  public headCountPercentage: number;
  public spending: number;
  public spendingPercentage: number;
  public dailySpendingPerCapita: number;
  public totalDailySpendingPerCapita: number;
  public dailySpendingPerCapitaPercentage: number;
  public totalAdressableBox: Box = new Box('', '', 'Demand');
  public totalSpendingIncomeBox: Box = new Box(
    '',
    '',
    'Percentage of Total Spending'
  );
  public totalSpendingIncomePerCapitaBox: Box = new Box(
    '',
    'Daily Spending per capita'
  );
  public largerThanAvgBox: Box = new Box('', '');
  public peopleBox: Box = new Box('', '', 'People');
  public totalPeopleBox = new Box('', '', 'People');
  public spendingIncomeBox: Box = new Box('', '', 'Spending');
  public spendingIncomePerCapitaBox: Box = new Box(
    '',
    '',
    'Spending per capita'
  );
  public dataFilters: DataFilters = new DataFilters();
  private ngUnsubscribe: Subject<void> = new Subject();
  public loadingCitiyConsumerSpending = false;
  public citiyConsumerSpending;
  public isFreemium = false;
  private cityRange = false;

  private startYearConditional = constants.startYear;
  public faLock = faLock;

  constructor(
    public dataService: DataService,
    private dataFiltersService: DataFiltersService,
    public authService: AuthService,
    public citiesService: CitiesService,
    public statisticsService: StatisticsService,
    private cdr: ChangeDetectorRef,
    private authResourcesService: AuthorizedResourcesService
  ) {}

  public ngOnInit(): void {
    combineLatest([
      this.dataFiltersService.getDataFilters(),
      this.dataService.getData(),
      this.dataFiltersService.valueChanges(),
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([filters, data, changes]) => {
        this.dataFilters = filters;
        const current = data.consumerSpendingData.current;

        if (current && current.spendingData && current.incomeData) {
          const res = (
            this.dataFilters.spendingGroup instanceof SpendingGroup
              ? (current as any).spendingData
              : (current as any).incomeData
          ) as ConsumerDataDetails;

          this.setConsumerData(res);
          this.cdr.detectChanges();
          return;
        }

        this.statisticsService.getConsumerData$.next({
          startYear: this.startYear,
          endYear: this.endYear,
        });

        if (this.dataFilters.regionSelection.isCitySelected()) {
          if (!this.isFreemium) {
            this.citiesService.getConsumerSpending$.next({
              startYear: this.startYear,
              endYear: this.endYear,
              city: this.dataFilters?.regionSelection?.getName(),
            });
          }
        }
        this.spendingIncomeBox.top = this.dataFilters.spendingGroup.type;
        this.spendingIncomePerCapitaBox.top = `${this.dataFilters.spendingGroup.type} per capita`;

        // Added conditions since the Yearly was changed to daily on freemium
        // for a brief moment before data loaded
        this.totalSpendingIncomePerCapitaBox.bottom =
          this.dataFilters.spendingPer === 0
            ? `Daily ${this.dataFilters.spendingGroup.type} per capita`
            : this.isFreemium
            ? `Yearly ${this.dataFilters.spendingGroup.type} per capita`
            : `Daily ${this.dataFilters.spendingGroup.type} per capita`;

        // Avoid ExpressionChangedAfterItHasBeenCheckedError
        this.cdr.detectChanges();

        if (
          data.consumerSpendingData.current &&
          data.consumerSpendingData.total &&
          Object.keys(data.consumerSpendingData.current).length > 0 &&
          !this.dataFilters.regionSelection.cities.size
        ) {
          this.totalDataCacheByYear = data.consumerSpendingData.total;
          this.dataCacheByYear = data.consumerSpendingData.current;
          this.calculateData(this.dataFilters.year);
          this.setTopBox();
          this.setMiddleBox();
          this.setRange();
          this.cityRange = false;
        }
        if (changes.regionSelection?.cities.size && !this.cityRange) {
          this.cityRange = true;
          this.initYears(2024);
          this.setRange();
        }
      });

    this.getCitiyConsumerSpending();

    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((plan) => {
        this.startYearConditional = plan.isFreemium
          ? constants.freemiumStartYear
          : constants.startYear;
        this.initYears(this.startYearConditional);
        this.isFreemium = plan.isFreemium;
        if (plan.isFreemium) {
          this.getStatisticsConsumerSpending();
        }
      });

    this.authService.categoriesEnabled$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isCategoriesEnabled) => {
        this.isCategoriesEnabled = isCategoriesEnabled;
      });
    // Avoid ExpressionChangedAfterItHasBeenCheckedError
    this.cdr.detectChanges();
  }

  private getStatisticsConsumerSpending() {
    this.statisticsService.getConsumerData$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(300),
        switchMap(({ startYear, endYear }) => {
          this.loadingCitiyConsumerSpending = true;
          return this.statisticsService
            .getConsumerData({ startYear, endYear })
            .pipe(tap(() => (this.loadingCitiyConsumerSpending = false)));
        })
      )
      .subscribe((response) => {
        const res =
          this.dataFilters.spendingGroup instanceof SpendingGroup
            ? response.spendingData
            : response.incomeData;
        this.setConsumerData(res);
      });
  }

  private setConsumerData(res: ConsumerDataDetails) {
    const worldName = DataUtils.getWorldName(this.dataFilters, 'the world');

    (this.totalPeopleBox = {
      top: 'People',
      middle: Utils.formatNumber(res.population, false, '.0-0'),
      bottom: `${Utils.formatNumber(
        res.compareToPopulationPercentage
      )} % of ${worldName} population`,
    }),
      (this.totalSpendingIncomeBox = {
        top:
          this.dataFilters.spendingPer === 0
            ? `Total daily  ${this.dataFilters.spendingGroup.type}`
            : `Total yearly  ${this.dataFilters.spendingGroup.type}`,
        middle: `$${Utils.formatNumber(res.amount, true)}`,
        bottom: `${Utils.formatNumber(
          res.compareToPercentage
        )}% of ${worldName} spending`,
      }),
      (this.totalSpendingIncomePerCapitaBox = {
        middle: `$${Utils.formatNumber(res.perCapitaAmount, false, '.2-2')}`,
        bottom:
          this.dataFilters.spendingPer === 0
            ? `Daily ${this.dataFilters.spendingGroup.type} per capita`
            : `Yearly ${this.dataFilters.spendingGroup.type} per capita`,
      }),
      (this.largerThanAvgBox = {
        middleColor:
          res.compareToPerCapitaAmount < 0
            ? Utils.getColorHex({
                name: ColorNames.Red,
                shade: ColorShades.Shade500,
              })
            : undefined,
        middle: `${
          res.compareToPerCapitaPercentage > 0 ? '+' : ''
        }${Utils.formatNumber(res.compareToPerCapitaPercentage)}%`,
        bottom: `compared to $${Utils.formatNumber(
          res.compareToPerCapitaAmount,
          false,
          '.2-2'
        )} ${worldName} avg`,
      }),
      (this.peopleGrowth = res.annualGrowthPopulation);
    (this.spendingGrowth = res.annualGrowthAmount),
      (this.spendingPerCapitaGrowth = res.annualGrowthPercentage);
  }

  // assuming on freemium we do not show historical data
  yearCondition(year: number) {
    if (!this.isFreemium) {
      return false;
    }
    return !([2030, 2034].includes(year) || year <= constants.currentYear + 1);
  }

  private getCitiyConsumerSpending() {
    this.citiesService.getConsumerSpending$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(debounceTimer),
        switchMap(({ startYear, endYear }) => {
          this.loadingCitiyConsumerSpending = true;
          return this.statisticsService
            .getConsumerData({ startYear, endYear })
            .pipe(tap(() => (this.loadingCitiyConsumerSpending = false)));
        })
      )
      .subscribe((response) => {
        const res =
          this.dataFilters.spendingGroup instanceof SpendingGroup
            ? response.spendingData
            : response.incomeData;
        this.setConsumerData(res);
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private setRange() {
    if (this.dataFilters.year > this.currentYear) {
      if (this.startYear !== this.currentYear) {
        this.setStartYear(this.currentYear);
      }
      this.setEndYear(this.dataFilters.year);
    } else if (this.dataFilters.year < this.currentYear) {
      if (this.endYear !== this.currentYear) {
        this.setEndYear(this.currentYear);
      }
      this.setStartYear(this.dataFilters.year);
    } else {
      this.setStartYear(this.currentYear);
      this.setEndYear(this.currentYear + 1);
    }

    this.setBottomBox();
  }

  private calculateData(year: number) {
    this.headCount = this.getHeadCount(year);
    this.headCountPercentage = this.getHeadCountPercentage(year);
    this.spending = this.getSpending(year);
    this.spendingPercentage = this.getSpendingPercentage();
    this.dailySpendingPerCapita = this.getDailySpendingPerCapita(year);
    this.totalDailySpendingPerCapita = this.getTotalDailySpendingPerCapita();
    this.dailySpendingPerCapitaPercentage =
      this.getDailySpendingPerCapitaPercentage();
    this.spendingGrowth = Utils.annualAverageGrowthRate(
      this.getSpending(this.dataFilters.year),
      this.getSpending(this.endYear),
      this.endYear - this.startYear
    );
  }

  private getHeadCount(year: number) {
    return this.dataCacheByYear[year]?.headCount ?? 0;
  }

  private getTotalHeadCount() {
    return this.totalDataCacheByYear[this.dataFilters.year]?.headCount ?? -1;
  }

  private getHeadCountPercentage(year: number) {
    return (100 * this.getHeadCount(year)) / this.getTotalHeadCount();
  }
  private getSpendingValue(ppp: number, nominal: number, defaultV: number) {
    let value = this.dataFilters.currency
      ? nominal ?? defaultV
      : ppp ?? defaultV;

    return value;
  }
  private getSpending(year: number) {
    let divider = this.dataFilters.spendingPer ? 1 : Utils.getDaysInAYear(year);
    let yearlySpending = this.dataFilters.currency
      ? this.dataCacheByYear[year]?.spending2021ppp ?? 0
      : this.dataCacheByYear[year]?.spendingCurrentUsd ?? 0;

    return yearlySpending / divider;
  }

  private getTotalSpending() {
    let totalSpending = this.dataFilters.currency
      ? this.totalDataCacheByYear[this.dataFilters.year]?.spending2021ppp ?? -1
      : this.totalDataCacheByYear[this.dataFilters.year]?.spendingCurrentUsd ??
        -1;

    let divider =
      this.dataFilters.spendingPer && totalSpending != -1
        ? 1
        : Utils.getDaysInAYear(this.dataFilters.year);

    return totalSpending / divider;
  }

  private getSpendingPercentage() {
    return (
      100 * (this.getSpending(this.dataFilters.year) / this.getTotalSpending())
    );
  }

  private getDailySpendingPerCapita(year: number) {
    let divider = this.dataFilters.spendingPer
      ? this.getHeadCount(year) * Utils.getDaysInAYear(year)
      : this.getHeadCount(year);

    return this.getSpending(year) / divider;
  }

  private getTotalDailySpendingPerCapita() {
    let divider = this.dataFilters.spendingPer
      ? this.getTotalHeadCount() * Utils.getDaysInAYear(this.dataFilters.year)
      : this.getTotalHeadCount();

    return this.getTotalSpending() / divider;
  }
  private getDailySpendingPerCapitaPercentage(): number {
    return (
      ((this.getDailySpendingPerCapita(this.dataFilters.year) -
        this.getTotalDailySpendingPerCapita()) /
        this.getTotalDailySpendingPerCapita()) *
      100
    );
  }

  changeStartYearOpen() {
    this.startYearOpen = !this.startYearOpen;
  }

  changeEndYearOpen() {
    this.endYearOpen = !this.endYearOpen;
  }

  setTopBox() {
    const worldName = DataUtils.getWorldName(this.dataFilters, 'the world');
    this.totalSpendingIncomeBox.top =
      this.dataFilters.spendingPer === 0
        ? `Total daily  ${this.dataFilters.spendingGroup.type}`
        : `Total yearly  ${this.dataFilters.spendingGroup.type}`;
    this.totalPeopleBox.middle = Utils.formatNumber(
      this.headCount,
      false,
      '.0-0'
    );
    this.totalPeopleBox.bottom = `${Utils.formatNumber(
      this.headCountPercentage
    )}% of ${worldName} population`;
    this.totalSpendingIncomeBox.middle = `$${Utils.formatNumber(
      this.spending,
      true
    )}`;
    this.totalSpendingIncomeBox.bottom = `${Utils.formatNumber(
      this.spendingPercentage
    )}% of ${worldName} ${this.dataFilters.spendingGroup.type}`;
  }

  setMiddleBox() {
    this.totalSpendingIncomePerCapitaBox.middle = `$${Utils.formatNumber(
      this.dailySpendingPerCapita,
      false,
      '.2-2'
    )}`;
    this.largerThanAvgBox.middle = `${
      this.dailySpendingPerCapitaPercentage > 0 ? '+' : ''
    }${Utils.formatNumber(this.dailySpendingPerCapitaPercentage)}%`;
    this.largerThanAvgBox.middleColor =
      this.dailySpendingPerCapitaPercentage < 0
        ? Utils.getColorHex({
            name: ColorNames.Red,
            shade: ColorShades.Shade500,
          })
        : undefined;
    this.largerThanAvgBox.bottom = `compared to $${Utils.formatNumber(
      this.totalDailySpendingPerCapita,
      false,
      '.2-2'
    )} ${DataUtils.getWorldName(this.dataFilters, 'world')} avg`;
  }

  setBottomBox() {
    this.setGrowthChanges();
    const people = `${this.peopleGrowth > 0 ? '+' : ''}${Utils.formatNumber(
      this.peopleGrowth
    )}%`;
    const spending = `${this.spendingGrowth > 0 ? '+' : ''}${Utils.formatNumber(
      this.spendingGrowth
    )}%`;
    const spc = `${
      this.spendingPerCapitaGrowth > 0 ? '+' : ''
    }${Utils.formatNumber(this.spendingPerCapitaGrowth)}%`;
    this.spendingIncomeBox.middle = spending;
    this.spendingIncomePerCapitaBox.top = `${this.dataFilters.spendingGroup.type} per capita`;
    this.spendingIncomePerCapitaBox.middle = spc;
    this.totalPeopleBox.middle = Utils.formatNumber(
      this.getHeadCount(this.dataFilters.year),
      false,
      '.0-0'
    );
    this.totalSpendingIncomeBox.middle = `$${Utils.formatNumber(
      this.getSpending(this.dataFilters.year),
      true
    )}`;
    this.totalSpendingIncomePerCapitaBox.middle = `$${Utils.formatNumber(
      this.getDailySpendingPerCapita(this.dataFilters.year),
      false,
      '.2-2'
    )}`;
    this.largerThanAvgBox.middle = `${
      this.getDailySpendingPerCapitaPercentage() > 0 ? '+' : ''
    }${Utils.formatNumber(this.getDailySpendingPerCapitaPercentage())}%`;
  }

  setStartYear(year: number, updateBottomBox: boolean = true) {
    if (this.years[this.years.length - 1] === year) {
      year = year - 1;
    }
    this.startYear = year;
    this.endYears = this.years.filter((x) => x > year);
    this.startYearOpen = false;
    if (updateBottomBox) {
      this.currentYear = year;
      if (this.isFreemium) {
        this.statisticsService.getConsumerData$.next({
          startYear: this.startYear,
          endYear: this.endYear,
        });
        return;
      }
      this.dataFilters?.regionSelection?.isCitySelected()
        ? this.citiesService.getConsumerSpending$.next({
            startYear: this.startYear,
            endYear: this.endYear,
            city: this.dataFilters?.regionSelection?.getName(),
          })
        : this.setBottomBox();
    }
  }

  setEndYear(year: number, updateBottomBox: boolean = true) {
    this.endYear =
      year > this.authResourcesService.endYear
        ? this.authResourcesService.endYear
        : year;
    this.startYears = this.years.filter((x) => x < year);
    this.endYearOpen = false;
    if (updateBottomBox) {
      this.currentYear = year;
      if (this.isFreemium) {
        this.statisticsService.getConsumerData$.next({
          startYear: this.startYear,
          endYear: this.endYear,
        });
        return;
      }
      this.dataFilters?.regionSelection?.isCitySelected()
        ? this.citiesService.getConsumerSpending$.next({
            startYear: this.startYear,
            endYear: this.endYear,
            city: this.dataFilters?.regionSelection?.getName(),
          })
        : this.setBottomBox();
    }
  }

  setGrowthChanges() {
    let yearDiff = this.endYear - this.startYear;

    this.peopleGrowth = Utils.annualAverageGrowthRate(
      this.getHeadCount(this.startYear),
      this.getHeadCount(this.endYear),
      yearDiff
    );

    this.spendingGrowth = Utils.annualAverageGrowthRate(
      this.getSpending(this.startYear),
      this.getSpending(this.endYear),
      yearDiff
    );

    this.spendingPerCapitaGrowth = Utils.annualAverageGrowthRate(
      this.getDailySpendingPerCapita(this.startYear),
      this.getDailySpendingPerCapita(this.endYear),
      yearDiff
    );
  }

  public getInfoBoxTextColor(): string {
    switch (this.dataFilters.gender) {
      case 'male':
        return '#458461';
      case 'female':
        return '#A84680';
      default:
        return '#29458C';
    }
  }

  private initYears(startYear?: number): void {
    const _startYear = startYear || 2016;
    this.years = Array.from(
      //Calculate length base on startYear and endYear
      { length: this.authResourcesService.endYear - _startYear + 1 },
      (_, i) => i + _startYear
    );
    this.startYears = this.years.slice(0, -1);
    this.endYears = this.years.slice(1);
  }
}
